import React, { useEffect, useRef, useState } from "react";
import { Search, Check, X, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import api from "../lib/api";

/**
 * Searchable dropdown for toner cartridge model numbers, sourced from the
 * 571-row compatibility database (`/api/compat/toners`). Same-brand models
 * float to the top when `brand` is set. Dealer can also free-type a custom
 * model if their cartridge isn't catalogued yet — emits onSelect(model, []).
 *
 * Props
 *   value         string   currently-selected model (controlled)
 *   onChange(v)   fn       called on every keystroke (for free-typed values)
 *   onSelect(model, printers[])  called when dealer picks a catalogued model.
 *                              `printers` is the array of compatible printer
 *                              full names (e.g. ["HP LaserJet P1007", ...])
 *                              so the caller can auto-populate "Suitable for".
 *   brand         string   floats matching-brand toners to top
 *   testIdPrefix  string
 *   placeholder   string
 *   required      bool
 */
export default function TonerModelSearchSelect({
    value,
    onChange,
    onSelect,
    brand = "",
    testIdPrefix = "toner-model",
    placeholder = "Search cartridge model — Q2612A, CB388A, TN-2365…",
    required = false,
}) {
    const [open, setOpen] = useState(false);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeIdx, setActiveIdx] = useState(-1);
    const [submitting, setSubmitting] = useState(false);
    const containerRef = useRef(null);
    const debounceRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const onDocClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    // Debounced search
    useEffect(() => {
        if (!open) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({ q: value || "", limit: "30" });
                if (brand) params.set("brand", brand);
                const { data } = await api.get(`/compat/toners?${params.toString()}`);
                setResults(Array.isArray(data) ? data : []);
                setActiveIdx(-1);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 180);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [value, brand, open]);

    // Wave 97 — auto-suggest Suitable-For when the dealer types/pastes an exact
    // catalogued model number (e.g. "CF226A" or "TN-2280") without clicking a
    // dropdown option. Calls onSelect with the compatible printer list so the
    // caller can pre-populate the "Suitable for" field. Fires once per unique
    // typed value via a ref-tracked guard so it doesn't loop.
    const lastAutoRef = useRef("");
    useEffect(() => {
        const typed = (value || "").trim();
        if (!typed || typed.length < 3) return;
        if (lastAutoRef.current === typed.toLowerCase()) return;
        const handle = setTimeout(async () => {
            try {
                const { data } = await api.get(`/compat/lookup-by-toner?model=${encodeURIComponent(typed)}`);
                const printers = Array.isArray(data?.printers) ? data.printers : [];
                if (printers.length > 0 && data?.model) {
                    lastAutoRef.current = typed.toLowerCase();
                    onSelect && onSelect(data.model, printers);
                }
            } catch {
                // silent — caller already lets the dealer free-type
            }
        }, 450);
        return () => clearTimeout(handle);
    }, [value, onSelect]);

    const pickFromDb = async (item) => {
        onChange(item.model);
        setOpen(false);
        // Custom (dealer-added) models have no printer compat list yet.
        if (item.is_custom) {
            onSelect && onSelect(item.model, []);
            return;
        }
        // Fetch full toner detail to get the compatible printers list
        try {
            const { data } = await api.get(`/compat/toner/${encodeURIComponent(item.model)}`);
            const printers = Array.isArray(data?.printers) ? data.printers : [];
            onSelect && onSelect(item.model, printers);
        } catch {
            onSelect && onSelect(item.model, []);
        }
    };

    const submitCustom = async () => {
        const model = (value || "").trim();
        if (!model || !brand) {
            toast.error(brand ? "Type the cartridge model first" : "Pick a brand first, then add a custom model");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/compat/custom-toner", { brand, model });
            toast.success("Saved! Your model is now available as a suggestion.");
            onChange(data.model || model);
            onSelect && onSelect(data.model || model, []);
            setOpen(false);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not save custom model";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const onKeyDown = (e) => {
        if (!open) {
            if (e.key === "ArrowDown") { setOpen(true); e.preventDefault(); }
            return;
        }
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
        else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); pickFromDb(results[activeIdx]); }
        else if (e.key === "Escape") { setOpen(false); }
    };

    const clear = () => {
        onChange("");
        onSelect && onSelect("", []);
        setOpen(false);
    };

    return (
        <div className="relative" ref={containerRef}>
            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B] pointer-events-none z-10" />
                <Input
                    type="text"
                    value={value || ""}
                    onChange={(e) => { onChange(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    required={required}
                    placeholder={placeholder}
                    className="tc-input-lg"
                    style={{ paddingLeft: 36, paddingRight: 36 }}
                    data-testid={`${testIdPrefix}-input`}
                    autoComplete="off"
                />
                {value ? (
                    <button
                        type="button"
                        onClick={clear}
                        aria-label="Clear"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#86868B] hover:text-[#0A0A0B] p-1"
                        data-testid={`${testIdPrefix}-clear`}
                    >
                        <X size={14} />
                    </button>
                ) : null}
            </div>

            {open && (
                <div
                    className="absolute z-30 left-0 right-0 mt-1 bg-white rounded-lg border border-black/[0.08] tc-shadow-lg max-h-72 overflow-y-auto"
                    data-testid={`${testIdPrefix}-dropdown`}
                >
                    {loading ? (
                        <div className="px-3 py-3 text-[12.5px] text-[#86868B]">Searching…</div>
                    ) : results.length === 0 ? (
                        <div className="px-3 py-3" data-testid={`${testIdPrefix}-empty`}>
                            <div className="text-[12.5px] text-[#86868B]">
                                No catalogued model matches{value ? <> for <span className="font-mono text-[#0A0A0B]">{value}</span></> : null}.
                            </div>
                            {value && brand ? (
                                <button
                                    type="button"
                                    onClick={submitCustom}
                                    disabled={submitting}
                                    className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#0A6E78] hover:text-[#00838f] bg-[#ECFBFD] border border-[#C2EFF5] rounded-md px-2.5 py-1.5 disabled:opacity-60"
                                    data-testid={`${testIdPrefix}-add-custom`}
                                >
                                    <PlusCircle size={13} />
                                    {submitting ? "Saving…" : <>Add &ldquo;<span className="font-mono">{brand} {value}</span>&rdquo; as new model</>}
                                </button>
                            ) : (
                                <div className="mt-1 text-[11.5px] text-[#86868B]">
                                    Pick a brand first to save this as a custom model.
                                </div>
                            )}
                        </div>
                    ) : (
                        <ul role="listbox" data-testid={`${testIdPrefix}-options`}>
                            {results.map((r, i) => {
                                const isActive = i === activeIdx;
                                const isSelected = (value || "").trim().toLowerCase() === r.model.toLowerCase();
                                const sameBrand = brand && r.brand?.toLowerCase() === brand.toLowerCase();
                                return (
                                    <li
                                        key={r.slug || r.model}
                                        role="option"
                                        aria-selected={isSelected}
                                        onMouseDown={(e) => { e.preventDefault(); pickFromDb(r); }}
                                        onMouseEnter={() => setActiveIdx(i)}
                                        className={`px-3 py-2 cursor-pointer flex items-center gap-2 text-[13px] ${isActive ? "bg-[#FFF8E1]" : "hover:bg-[#FAFAFB]"} border-b border-black/[0.04] last:border-b-0`}
                                        data-testid={`${testIdPrefix}-option-${i}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="font-mono font-semibold text-[13px] text-[#0A0A0B] truncate">{r.model}</div>
                                            <div className="text-[11px] text-[#6E6E73] truncate">
                                                {r.brand}{r.type ? ` · ${r.type}` : ""}
                                                {sameBrand && <span className="ml-1.5 inline-block px-1.5 py-[1px] bg-emerald-50 text-emerald-700 rounded text-[10px] font-semibold uppercase tracking-wide">Same brand</span>}
                                                {r.is_custom && <span className="ml-1.5 inline-block px-1.5 py-[1px] bg-amber-50 text-amber-700 border border-amber-200 rounded text-[10px] font-semibold uppercase tracking-wide" data-testid={`${testIdPrefix}-custom-badge`}>Added by dealer</span>}
                                            </div>
                                        </div>
                                        {isSelected && <Check size={14} className="text-emerald-600 shrink-0" />}
                                    </li>
                                );
                            })}
                            {value && brand && !results.some((r) => r.model.toLowerCase() === (value || "").trim().toLowerCase()) ? (
                                <li className="px-3 py-2 border-t border-black/[0.06] bg-[#FAFAFB]">
                                    <button
                                        type="button"
                                        onClick={submitCustom}
                                        disabled={submitting}
                                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#0A6E78] hover:text-[#00838f] disabled:opacity-60"
                                        data-testid={`${testIdPrefix}-add-custom-inline`}
                                    >
                                        <PlusCircle size={13} />
                                        {submitting ? "Saving…" : <>Don&rsquo;t see it? Add &ldquo;<span className="font-mono">{brand} {value}</span>&rdquo; as new</>}
                                    </button>
                                </li>
                            ) : null}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
